import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { EmptyState as ToqioEmptyState } from '@toqio/toqio-web-components';
import { useBridge } from '../../bridge/useBridge';
import { placeholderUrl } from '../assets';

/** Generic tenant used when the customer has no placeholder of its own. */
const FALLBACK_TENANT = 'toqio';

/** Centers the DS empty state in the available width (it renders left by default). */
const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

/** Illustration asset per variant (mirrors lum-web's empty vs error placeholders). */
const ILLUSTRATION = { empty: 'empty_state.png', error: 'error.png' } as const;

type EmptyStateAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'accent' | 'critical';
};

type Props = {
  title?: string;
  description: string;
  /**
   * `error` swaps the generic placeholder for the error illustration — the
   * lum-web `EmptyErrorState` pattern, used for load failures instead of a red
   * inline banner.
   */
  variant?: 'empty' | 'error';
  /** Optional action button (e.g. Retry), rendered by the DS as `firstButton`. */
  action?: EmptyStateAction;
};

/**
 * DS EmptyState with a tenant-aware illustration: loads the customer's CDN
 * placeholder and falls back to the generic `toqio` asset when it's missing.
 * `variant="error"` uses the error illustration (lum-web's `EmptyErrorState`).
 */
export const EmptyState: React.FC<Props> = ({
  title,
  description,
  variant = 'empty',
  action,
}) => {
  const { tenant } = useBridge();
  const name = ILLUSTRATION[variant];
  const tenantSrc = placeholderUrl(tenant ?? FALLBACK_TENANT, name);
  const fallbackSrc = placeholderUrl(FALLBACK_TENANT, name);
  // Start on the safe fallback and promote to the tenant asset only once the
  // probe confirms it loads — the DS never paints a broken/404 URL.
  const [illustration, setIllustration] = useState(fallbackSrc);

  useEffect(() => {
    if (tenantSrc === fallbackSrc) return;
    let active = true;
    const probe = new Image();
    probe.onload = () => {
      if (active) setIllustration(tenantSrc);
    };
    probe.src = tenantSrc;
    return () => {
      active = false;
    };
  }, [tenantSrc, fallbackSrc]);

  return (
    <Center>
      <ToqioEmptyState
        illustration={illustration}
        title={title}
        description={description}
        firstButton={
          action
            ? {
                label: action.label,
                children: action.label,
                onClick: action.onClick,
                variant: action.variant ?? 'secondary',
                size: 'm',
              }
            : undefined
        }
      />
    </Center>
  );
};
